// useSvgViewport — Pan/Zoom Hook für SVG
// Wheel = Zoom, Space+Drag oder Mittelklick-Drag = Pan
import { useRef, useState, useCallback, useEffect } from 'react';

export function useSvgViewport({ contentWidth = 1200, contentHeight = 800, enableDragPan = true } = {}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);

  // viewBox: { x, y, w, h } — controls zoom/pan
  const [viewBox, setViewBox] = useState({
    x: 0, y: 0, w: contentWidth, h: contentHeight,
  });

  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef(null);
  const [spaceDown, setSpaceDown] = useState(false);

  // Reset on content size change
  useEffect(() => {
    setViewBox({ x: 0, y: 0, w: contentWidth, h: contentHeight });
  }, [contentWidth, contentHeight]);

  // Space-Key zum Panen
  useEffect(() => {
    const isEditableTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };
    const down = (e) => {
      if (e.code === 'Space' && !isEditableTarget(e.target)) {
        e.preventDefault();
        setSpaceDown(true);
      }
    };
    const up = (e) => { if (e.code === 'Space') setSpaceDown(false); };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  // Zoom-Limits: 100% (passt vollständig in den Container) bis 500% Reinzoomen.
  // Rauszoomen über die Default-Ansicht hinaus ist nicht erlaubt — sonst hätte
  // der User das Gefühl "viel zu weit weggescrollt".
  const handleWheel = useCallback((e) => {
    if (!svgRef.current) return;
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width;
    const my = (e.clientY - rect.top) / rect.height;
    const factor = e.deltaY < 0 ? 0.9 : 1.1;
    // Untere Grenze: 20% der Default-Größe (5× reingezoomt).
    // Obere Grenze: Default (100%, kein Rauszoomen).
    const minW = contentWidth * 0.2, minH = contentHeight * 0.2;
    const maxW = contentWidth,        maxH = contentHeight;
    const newW = Math.max(minW, Math.min(maxW, viewBox.w * factor));
    const newH = Math.max(minH, Math.min(maxH, viewBox.h * factor));
    // Zoom an Mausposition fixieren
    let newX = viewBox.x + (viewBox.w - newW) * mx;
    let newY = viewBox.y + (viewBox.h - newH) * my;
    // ViewBox innerhalb des Inhalts halten
    newX = Math.max(0, Math.min(contentWidth - newW, newX));
    newY = Math.max(0, Math.min(contentHeight - newH, newY));
    setViewBox({ x: newX, y: newY, w: newW, h: newH });
  }, [viewBox, contentWidth, contentHeight]);

  const handlePointerDown = useCallback((e) => {
    // Mittelklick oder Space+Left = immer Pan
    if (e.button === 1 || (spaceDown && e.button === 0)) {
      e.preventDefault();
      setIsPanning(true);
      panStart.current = { mx: e.clientX, my: e.clientY, vb: { ...viewBox } };
      return;
    }
    // Reingezoomt + Drag-Pan aktiviert → Left-Drag pant (bequemes Navigieren ohne Space)
    const isZoomed = viewBox.w < contentWidth - 0.5 || viewBox.h < contentHeight - 0.5;
    if (enableDragPan && isZoomed && e.button === 0) {
      e.preventDefault();
      setIsPanning(true);
      panStart.current = { mx: e.clientX, my: e.clientY, vb: { ...viewBox } };
    }
  }, [spaceDown, viewBox, contentWidth, contentHeight, enableDragPan]);

  useEffect(() => {
    if (!isPanning) return;
    const move = (e) => {
      if (!panStart.current || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const dx = (e.clientX - panStart.current.mx) * (panStart.current.vb.w / rect.width);
      const dy = (e.clientY - panStart.current.my) * (panStart.current.vb.h / rect.height);
      const vb = panStart.current.vb;
      // ViewBox-Position innerhalb des Inhalts halten
      let nx = vb.x - dx;
      let ny = vb.y - dy;
      nx = Math.max(0, Math.min(contentWidth - vb.w, nx));
      ny = Math.max(0, Math.min(contentHeight - vb.h, ny));
      setViewBox({ x: nx, y: ny, w: vb.w, h: vb.h });
    };
    const up = () => { setIsPanning(false); panStart.current = null; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [isPanning, contentWidth, contentHeight]);

  const resetView = useCallback(() => {
    setViewBox({ x: 0, y: 0, w: contentWidth, h: contentHeight });
  }, [contentWidth, contentHeight]);

  const zoomBy = useCallback((factor) => {
    setViewBox((v) => {
      const minW = contentWidth * 0.2, minH = contentHeight * 0.2;
      const maxW = contentWidth,        maxH = contentHeight;
      const newW = Math.max(minW, Math.min(maxW, v.w * factor));
      const newH = Math.max(minH, Math.min(maxH, v.h * factor));
      let nx = v.x + (v.w - newW) / 2;
      let ny = v.y + (v.h - newH) / 2;
      nx = Math.max(0, Math.min(contentWidth - newW, nx));
      ny = Math.max(0, Math.min(contentHeight - newH, ny));
      return { x: nx, y: ny, w: newW, h: newH };
    });
  }, [contentWidth, contentHeight]);

  const zoomPercent = Math.round((contentWidth / viewBox.w) * 100);

  return {
    svgRef,
    containerRef,
    viewBox,
    viewBoxString: `${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`,
    handleWheel,
    handlePointerDown,
    isPanning,
    spaceDown,
    resetView,
    zoomBy,
    zoomPercent,
  };
}

export default useSvgViewport;
